import { useState, type FormEvent } from "react";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";

type Status = "idle" | "loading" | "success" | "error";

const SUBMIT_URL = "https://readdy.ai/api/form/dalu2gth599pccsgtukg";

const offerings = [
  "Farm walks & field trails",
  "Harvesting experiences",
  "Farm-to-table cooking",
  "Village & cultural walks",
  "School & student visits",
  "Family & children activities",
  "Orchard & plantation tours",
  "Dairy & livestock visits",
];

export default function PartnerForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("Thank you! Your application has been received. We'll be in touch soon.");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, value.toString());
    });

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage(
          "Thank you! Your application has been received. Our team will contact you within 2–3 working days.",
        );
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "Something went wrong. Please try again.",
        );
      }
    } catch {
      setStatus("error");
      setMessage("Network error. Please check your connection and try again.");
    }
  };

  const inputClass =
    "w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400";
  const labelClass = "block text-sm font-medium text-foreground-800 mb-2";

  return (
    <section id="partner-form" className="sun-glaze bg-background-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-stretch">
          <Reveal className="lg:col-span-8">
            <div className="h-full bg-background-50 rounded-lg border border-background-200 p-7 md:p-9">
              <div className="flex items-center gap-3 mb-5">
                <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
                <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                  Application
                </span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
                Become a Farm Partner
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                Tell us a little about your farm and what you'd like to share. There's no obligation
                — we'll call you to talk it through.
              </p>

              <form
                id="rythugaru-partner-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-7 space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="pt-name" className={labelClass}>
                      Your name
                    </label>
                    <input
                      id="pt-name"
                      name="name"
                      type="text"
                      required
                      placeholder="e.g. Rajanna"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="pt-phone" className={labelClass}>
                      Phone / WhatsApp
                    </label>
                    <input
                      id="pt-phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="+91 ..."
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="pt-email" className={labelClass}>
                      Email
                    </label>
                    <input
                      id="pt-email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="pt-village" className={labelClass}>
                      Village / Location
                    </label>
                    <input
                      id="pt-village"
                      name="village"
                      type="text"
                      required
                      placeholder="e.g. Ravulapalem"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="pt-district" className={labelClass}>
                      District
                    </label>
                    <input
                      id="pt-district"
                      name="district"
                      type="text"
                      placeholder="e.g. East Godavari"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="pt-area" className={labelClass}>
                      Farm size (acres)
                    </label>
                    <input
                      id="pt-area"
                      name="farm_size"
                      type="text"
                      placeholder="e.g. 5 acres"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="pt-years" className={labelClass}>
                      Farming for
                    </label>
                    <input
                      id="pt-years"
                      name="farming_years"
                      type="text"
                      placeholder="e.g. 20 years"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="pt-crops" className={labelClass}>
                    Main crops & livestock
                  </label>
                  <input
                    id="pt-crops"
                    name="crops"
                    type="text"
                    required
                    placeholder="e.g. Paddy, coconut, banana, cows"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label htmlFor="pt-offer" className={labelClass}>
                    What would you like to offer visitors?
                  </label>
                  <select
                    id="pt-offer"
                    name="offering"
                    className={`${inputClass} cursor-pointer`}
                  >
                    {offerings.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="pt-guests" className={labelClass}>
                      Guests you can host at a time
                    </label>
                    <input
                      id="pt-guests"
                      name="guest_capacity"
                      type="text"
                      placeholder="e.g. up to 20"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="pt-language" className={labelClass}>
                      Languages spoken
                    </label>
                    <input
                      id="pt-language"
                      name="languages"
                      type="text"
                      placeholder="e.g. Telugu, English"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="pt-message" className={labelClass}>
                    Tell us about your farm & story
                  </label>
                  <textarea
                    id="pt-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder="Share a little about your family, your land and what makes your farm special."
                    className={`${inputClass} resize-none`}
                  ></textarea>
                </div>

                <div className="form-extra-contact" aria-hidden="true">
                  <label htmlFor="pt-website-alt">Leave this field empty</label>
                  <input
                    id="pt-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {status === "loading" ? "Submitting..." : "Submit Application"}
                  {status !== "loading" && <i className="ri-send-plane-fill"></i>}
                </button>

                {status === "success" && (
                  <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                    <span>{message}</span>
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-start gap-2 text-sm text-accent-800 bg-accent-100 border border-accent-300 rounded-md px-4 py-3">
                    <i className="ri-error-warning-fill text-lg"></i>
                    <span>{message}</span>
                  </p>
                )}
              </form>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-4" delay={120}>
            <div className="h-full flex flex-col gap-6">
              <div className="bg-primary-900 rounded-lg p-7 md:p-8">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-question-answer-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-2xl text-background-50 font-semibold mt-5">
                  What Happens Next
                </h3>
                <div className="mt-6 space-y-5">
                  {[
                    { icon: "ri-checkbox-circle-line", text: "We review your application" },
                    { icon: "ri-phone-line", text: "A short call to understand your farm" },
                    { icon: "ri-calendar-line", text: "A friendly visit at your convenience" },
                    { icon: "ri-plant-line", text: "We set up your listing together" },
                  ].map((item, index) => (
                    <div key={item.text} className="flex items-start gap-4">
                      <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-sm font-semibold shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-sm text-background-100 leading-relaxed pt-1.5">
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 rounded-lg border border-background-200 p-7 md:p-8 flex-1">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-heart-3-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">
                  Our Promise
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mt-4">
                  There is no fee to apply and no pressure to say yes. Partner only if it feels
                  right for you and your family — you stay in control of your farm, your dates and
                  your story.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}